import { X } from "lucide-react";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Checkbox } from "@/components/ui/checkbox";

export const emptyMatter = { matter: "", instrument: "", ask: "" };
export const matterTouched = (m) => Object.values(m).some((v) => v.trim());
export const matterComplete = (m) => Object.values(m).every((v) => v.trim());

const RULES = ["Name the instrument.", "Describe what happened, not how it felt.", "Never speculate about motive.", "Ask for something they can actually do."];
const CHECKS = ["I have named the instrument this arises under", "I have described what happened rather than how I feel about it", "I have not suggested why anyone did what they did"];
const FIELDS = [
  { key: "matter", label: "What is the matter?", hint: "One or two plain sentences. What has happened, or what is proposed.", max: 300 },
  { key: "instrument", label: "What is it under?", hint: "The Act, the section, the decision, the report, or the document reference. If you cannot name one, this is not ready to send.", max: 200 },
  { key: "ask", label: "What are you asking the member to do?", hint: "Something within their power as a federal member.", max: 200 },
];

export const LocalMatterBlock = ({ matter, setMatter, jurisdiction, setJurisdiction, checks, setChecks, onRemove }) => (
  <section className="rounded-2xl bg-[#FAF8F2] border border-[#8FA58A]/50 p-5 sm:p-7 space-y-6" data-testid="local-matter-block">
    <div className="flex items-start justify-between gap-4">
      <div>
        <p className="font-type text-[11px] uppercase tracking-[0.3em] text-[#5A7355]">One local matter</p>
        <h3 className="font-type font-bold text-base md:text-lg text-[#1B2A41] mt-1">Turn it into something an electorate office must answer</h3>
      </div>
      <button type="button" onClick={onRemove} data-testid="local-matter-remove" className="text-xs text-[#5C6A79] hover:text-[#E2705A] inline-flex items-center gap-1"><X className="w-3.5 h-3.5" /> Remove</button>
    </div>

    <ol className="grid sm:grid-cols-2 gap-2" data-testid="local-matter-rules">
      {RULES.map((r, i) => <li key={r} className="flex gap-2 text-xs text-[#1B2A41]"><span className="font-type font-bold text-[#E2705A]">{i + 1}.</span>{r}</li>)}
    </ol>

    <div className="grid lg:grid-cols-[1fr_minmax(260px,0.9fr)] gap-6">
      <div className="space-y-5">
        {FIELDS.map((f) => (
          <div key={f.key} className="space-y-1.5">
            <Label htmlFor={`lm-${f.key}`} className="text-xs font-semibold text-[#1B2A41]">{f.label} <span className="text-[#E2705A]">*</span></Label>
            <p className="text-[11px] text-[#5C6A79]">{f.hint}</p>
            <Textarea id={`lm-${f.key}`} data-testid={`local-matter-${f.key}`} value={matter[f.key]} maxLength={f.max} rows={f.key === "matter" ? 3 : 2} onChange={(e) => setMatter({ ...matter, [f.key]: e.target.value })} aria-required="true" className="bg-white border-[#D8D2C4] text-sm" />
            <p className="text-[10px] text-right text-[#5C6A79]">{matter[f.key].length}/{f.max}</p>
          </div>
        ))}
        <div className="space-y-1.5">
          <Label className="text-xs font-semibold text-[#1B2A41]">Whose responsibility is it?</Label>
          <div className="flex flex-wrap gap-2">
            {[["federal", "Federal"], ["state", "State / Territory"], ["council", "Council"]].map(([v, l]) => (
              <button key={v} type="button" onClick={() => setJurisdiction(v)} data-testid={`local-matter-jurisdiction-${v}`} className={`h-8 px-3 rounded-full text-xs border ${jurisdiction === v ? "bg-[#1B2A41] border-[#1B2A41] text-[#FDFBF7]" : "border-[#D8D2C4] text-[#1B2A41]"}`}>{l}</button>
            ))}
          </div>
          {jurisdiction !== "federal" && <p className="text-xs text-[#6B4F00] bg-[#FFF4D6] border border-[#E8C870] rounded-lg px-3 py-2" data-testid="local-matter-jurisdiction-note">A federal member can still make representations, but say so in your ask. Asking them to fix a State matter will get a form letter in reply.</p>}
        </div>
      </div>

      <aside className="rounded-xl bg-white border border-[#D8D2C4] p-4 text-xs space-y-3 self-start" data-testid="local-matter-example">
        <p className="font-type text-[10px] uppercase tracking-[0.25em] text-[#5A7355]">Worked example</p>
        <div>
          <p className="text-[#E2705A] font-semibold">Instead of this:</p>
          <p className="text-[#5C6A79] italic mt-1">"The council and the state government have destroyed this town with their approvals process and nobody in Canberra cares what happens to us out here."</p>
        </div>
        <div className="space-y-1.5">
          <p className="text-[#5A7355] font-semibold">Write this:</p>
          <p><strong>Matter:</strong> Three applications in this division have been refused on cultural heritage grounds since March, with no published reasons.</p>
          <p><strong>Instrument:</strong> Aboriginal and Torres Strait Islander Heritage Protection Act 1984, section 10.</p>
          <p><strong>Ask:</strong> Will you ask the Minister to publish the reasons for section 10 decisions as a matter of course?</p>
        </div>
      </aside>
    </div>

    <div className="space-y-2 pt-2 border-t border-[#D8D2C4]" data-testid="local-matter-checklist">
      <p className="text-xs font-semibold text-[#1B2A41]">Before you generate</p>
      {CHECKS.map((c, i) => (
        <label key={c} className="flex items-start gap-2 text-xs text-[#1B2A41] cursor-pointer">
          <Checkbox data-testid={`local-matter-check-${i}`} checked={checks[i]} onCheckedChange={(v) => setChecks(checks.map((x, j) => (j === i ? !!v : x)))} className="mt-0.5" /> {c}
        </label>
      ))}
    </div>
  </section>
);
